<template>
  <div class="ovg-form">
    <div class="ovg-consent__header">
      <h3 class="ovg-consent__title">Consentimiento de Servicio</h3>
      <p class="ovg-hint">
        Para continuar, debes leer y aceptar las condiciones del servicio. Haz
        clic para revisar el documento y luego firma a continuación dibuja tu
        firma con tu dedo o mouse
      </p>
    </div>

    <button
      type="button"
      class="ovg-consent__doc-btn"
      @click="mostrarDocumento = true"
    >
      <svg-icon type="mdi" :path="icons.documento" :size="24" />
      Leer Documento de Consentimiento
    </button>

    <div class="ovg-consent__signature">
      <label class="ovg-label">Firma*</label>
      <p class="ovg-hint">Dibuja tu firma aquí</p>
      <div class="ovg-consent__pad">
        <canvas ref="canvas" class="ovg-consent__canvas" />
        <svg-icon
          class="ovg-consent__pad-icon"
          type="mdi"
          :path="icons.pluma"
          :size="24"
        />
        <span class="ovg-consent__baseline" />
      </div>
      <div class="ovg-consent__actions">
        <button
          type="button"
          class="ovg-btn ovg-btn--xs ovg-btn--outline-navy"
          :class="{ 'ovg-btn--faded': guardada }"
          @click="limpiar"
        >
          Limpiar Todo
        </button>
        <button
          type="button"
          class="ovg-btn ovg-btn--xs ovg-btn--navy"
          :class="{ 'ovg-btn--faded': guardada }"
          :disabled="guardada"
          @click="guardar"
        >
          Guardar Firma
        </button>
      </div>
    </div>

    <div class="ovg-card__footer ovg-card__footer--between">
      <button type="button" class="ovg-btn ovg-btn--secondary" @click="goBack">
        Paso Anterior
      </button>
      <button
        type="button"
        class="ovg-btn ovg-btn--primary"
        :disabled="!guardada"
        @click="$emit('next')"
      >
        Siguiente Paso
      </button>
    </div>

    <div
      v-if="mostrarDocumento"
      class="ovg-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-doc-title"
    >
      <div class="ovg-modal ovg-modal--document">
        <div class="ovg-modal__header">
          <h3 id="consent-doc-title" class="ovg-modal__title">
            Consentimiento de Servicio de Revisión y Diagnóstico
          </h3>
          <button
            type="button"
            class="ovg-modal__close"
            aria-label="Cerrar"
            @click="mostrarDocumento = false"
          >
            <svg-icon type="mdi" :path="icons.cerrar" :size="24" />
          </button>
        </div>
        <iframe
          v-if="visorPdfDisponible"
          class="ovg-consent__viewer"
          :src="documentUrl"
          title="Documento de consentimiento"
        />
        <div v-else class="ovg-consent__viewer-fallback">
          <p class="ovg-modal__text">
            Tu navegador no permite previsualizar el documento.
          </p>
          <a
            class="ovg-btn ovg-btn--xs ovg-btn--navy"
            :href="documentUrl"
            target="_blank"
            rel="noopener"
          >
            Abrir documento
          </a>
        </div>
        <hr class="ovg-consent__separator" />
        <button
          type="button"
          class="ovg-btn ovg-btn--primary"
          @click="mostrarDocumento = false"
        >
          Cerrar y Volver a Firmar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import SignaturePad from 'signature_pad'
import SvgIcon from '@jamescoyle/vue-icon'
import {
  mdiFountainPenTip,
  mdiTextBoxMultipleOutline,
  mdiWindowClose,
} from '@mdi/js'

const MARGEN_RECORTE = 10

const recortarFirma = (canvas) => {
  const { width, height } = canvas
  const { data } = canvas.getContext('2d').getImageData(0, 0, width, height)
  let top = height
  let left = width
  let right = -1
  let bottom = -1
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] !== 0) {
        if (x < left) left = x
        if (x > right) right = x
        if (y < top) top = y
        if (y > bottom) bottom = y
      }
    }
  }
  if (right < 0) return null

  const x0 = Math.max(left - MARGEN_RECORTE, 0)
  const y0 = Math.max(top - MARGEN_RECORTE, 0)
  const ancho = Math.min(right + MARGEN_RECORTE, width - 1) - x0 + 1
  const alto = Math.min(bottom + MARGEN_RECORTE, height - 1) - y0 + 1
  const recorte = document.createElement('canvas')
  recorte.width = ancho
  recorte.height = alto
  recorte
    .getContext('2d')
    .drawImage(canvas, x0, y0, ancho, alto, 0, 0, ancho, alto)
  return recorte.toDataURL('image/png')
}

export default {
  name: 'ConsentRegions',
  components: {
    SvgIcon,
  },
  props: ['documentUrl', 'firma', 'setFirma'],
  data() {
    return {
      pad: null,
      guardada: false,
      mostrarDocumento: false,
      visorPdfDisponible: navigator.pdfViewerEnabled !== false,
      icons: {
        documento: mdiTextBoxMultipleOutline,
        pluma: mdiFountainPenTip,
        cerrar: mdiWindowClose,
      },
    }
  },
  mounted() {
    this.pad = new SignaturePad(this.$refs.canvas, {
      penColor: '#23201c',
      minWidth: 0.8,
      maxWidth: 2.5,
    })
    this.ajustarCanvas()
    window.addEventListener('resize', this.ajustarCanvas)

    if (this.firma && this.firma.puntos) {
      this.pad.fromData(this.firma.puntos)
      this.guardada = true
      this.pad.off()
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.ajustarCanvas)
    if (this.pad) {
      this.pad.off()
    }
  },
  methods: {
    ajustarCanvas() {
      const { canvas } = this.$refs
      if (!canvas || !this.pad) return
      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      const ancho = canvas.offsetWidth * ratio
      const alto = canvas.offsetHeight * ratio
      if (canvas.width === ancho && canvas.height === alto) return

      const trazos = this.pad.toData()
      canvas.width = ancho
      canvas.height = alto
      canvas.getContext('2d').scale(ratio, ratio)
      this.pad.clear()
      this.pad.fromData(trazos)
    },
    limpiar() {
      this.pad.clear()
      if (this.guardada) {
        this.pad.on()
        this.guardada = false
        this.setFirma(null)
      }
    },
    guardar() {
      const dataUrl = this.pad.isEmpty()
        ? null
        : recortarFirma(this.$refs.canvas)
      if (!dataUrl) {
        this.$bvToast.toast('Dibuja tu firma antes de guardarla', {
          variant: 'danger',
        })
        return
      }
      this.pad.off()
      this.guardada = true
      this.setFirma({ dataUrl, puntos: this.pad.toData() })
    },
    goBack() {
      this.$emit('goBack')
    },
  },
}
</script>
